import { z } from 'zod'

import {
  backupKindSchema,
  backupStatusSchema,
  bytesSchema,
  idSchema,
  isoDateSchema,
  isoInstantSchema,
  limitSchema,
  requestableBackupKindSchema,
} from '../common'

/**
 * O6–O9 · Backups and storage (G12, design 05).
 *
 * `BOOK_ONLY` is the ledger — small, fast, the thing that actually matters.
 * `FULL` includes the photos and videos and can be gigabytes. Both are offered
 * because a trader on a phone tether will take the book and leave the video.
 */
export const backupSchema = z.object({
  id: idSchema,
  kind: backupKindSchema,
  status: backupStatusSchema,
  byteSize: bytesSchema.nullish(),
  /** Row counts per table — the integrity check a restore is verified against. */
  rowCounts: z.record(z.string(), z.number().int().nonnegative()).nullish(),
  startedAt: isoInstantSchema,
  finishedAt: isoInstantSchema.nullish(),
  /** Backups expire; the storage they occupy is the trader's own quota. */
  expiresAt: isoInstantSchema.nullish(),
  requestedByName: z.string().nullish(),
  error: z.string().nullish(),
})

export const createBackupRequest = z.object({ kind: requestableBackupKindSchema })

export const backupListResponse = z.object({
  items: z.array(backupSchema),
  lastFullAt: isoInstantSchema.nullish(),
  lastNightlyStatus: backupStatusSchema.nullish(),
  /** How many nightly copies are kept, so the screen can say it plainly. */
  copiesKept: z.number().int().nonnegative(),
  automaticEnabled: z.boolean(),
  /** The hour the nightly job runs, in `Asia/Karachi`. Platform-configured. */
  nightlyHourPkt: z.number().int().min(0).max(23).nullish(),
})

/**
 * O7 · `GET /backups/:id/download` — a **signed, expiring** link, never a public
 * URL and never a guessable one (conflict C6). A man's herd is his business.
 */
export const backupDownloadResponse = z.object({
  url: z.string().min(1),
  expiresAt: isoInstantSchema,
  byteSize: bytesSchema,
})

/**
 * O8 · `POST /backups/:id/restore` — **takes a `PRE_RESTORE` snapshot first**,
 * then restores. A restore is the one operation that can destroy more than it
 * repairs, and the snapshot is the undo for a restore of the wrong file.
 */
export const restoreBackupRequest = z.object({
  /** The owner types the backup's date to confirm. Checked server-side. */
  confirmDate: isoDateSchema,
})

export const restoreBackupResponse = z.object({
  restoreJobId: idSchema,
  preRestoreBackupId: idSchema,
  status: backupStatusSchema,
})

/* ── O9 · GET /storage-usage ──────────────────────────────────────────────── */

export const storageUsageResponse = z.object({
  usedBytes: bytesSchema,
  limitBytes: limitSchema,
  mediaCount: z.number().int().nonnegative(),
  breakdown: z.array(
    z.object({
      label: z.string(),
      bytes: bytesSchema,
    }),
  ),
  /** Originals kept vs transcoded — the one lever that moves the bill. */
  originalsBytes: bytesSchema.optional(),
  savedByTranscodeBytes: bytesSchema.optional(),
})

export type Backup = z.infer<typeof backupSchema>
export type CreateBackupRequest = z.input<typeof createBackupRequest>
export type BackupListResponse = z.infer<typeof backupListResponse>
export type BackupDownloadResponse = z.infer<typeof backupDownloadResponse>
export type RestoreBackupRequest = z.input<typeof restoreBackupRequest>
export type StorageUsageResponse = z.infer<typeof storageUsageResponse>
